import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { dashboardService } from "../../services/dashboard.service";
import { SearchableSelect } from "../ui/SearchableSelect";
import ReactApexChart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";

type DateBasis = "billing_date" | "service_date";

export const JCodeARWidget: React.FC = () => {
  const [dateBasis, setDateBasis] = useState<DateBasis>("billing_date");
  const [selectedJCode, setSelectedJCode] = useState<string>("all");

  // Fetch J-Codes list for selector
  const { data: jCodes = [] } = useQuery({
    queryKey: ["jcodes"],
    queryFn: () => dashboardService.getJCodes(),
  });

  // Fetch metrics (refetch on date basis or J-Code change)
  const {
    data: metrics,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["jcode-ar-metrics", dateBasis, selectedJCode],
    queryFn: () =>
      dashboardService.getJCodeAR(
        dateBasis === "billing_date" ? "BILLING_PROVIDER_CREATED_DATE" : "CHARGE_CREATED_DATE",
        selectedJCode !== "all" ? selectedJCode : undefined
      ),
  });

  const chartData = useMemo(() => {
    if (!metrics || metrics.length === 0) return [];

    const bucketsMap: Record<string, number> = {};
    let totalValue: number | null = null;

    metrics.forEach((m) => {
      const key = m.metricName;
      const val = parseFloat(m.value?.toString() || "0");
      if (!isNaN(val)) {
        if (key === "total") {
          totalValue = val;
        } else {
          bucketsMap[key] = val;
        }
      }
    });

    const sortedBucketNames = Object.keys(bucketsMap).sort((a, b) => {
      if (a.startsWith(">")) return 1;
      if (b.startsWith(">")) return -1;
      return parseInt(a.split("-")[0]) - parseInt(b.split("-")[0]);
    });

    const totalSum =
      totalValue !== null
        ? totalValue
        : sortedBucketNames.reduce((sum, key) => sum + bucketsMap[key], 0);

    const result = sortedBucketNames.map((key) => {
      const value = bucketsMap[key];
      return {
        name: key.startsWith(">") ? key : key.split("-")[0],
        fullLabel: `${key} days`,
        value,
        percentage: totalSum > 0 ? (value / totalSum) * 100 : 0,
        isTotal: false,
      };
    });

    if (totalValue !== null) {
      result.push({
        name: "Totals",
        fullLabel: "Totals",
        value: totalValue,
        percentage: 100,
        isTotal: true,
      });
    }

    return result;
  }, [metrics]);

  const formatCurrency = (value: number): string => {
    return `$${value.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const chartBuckets = chartData.filter((d) => !d.isTotal);

  const series = [
    {
      name: "Amount",
      data: chartBuckets.map((d) => d.value),
    },
  ];

  const options: ApexOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      animations: { enabled: true },
    },
    grid: { strokeDashArray: 3 },
    colors: ["#6B9FBD"],
    plotOptions: {
      bar: {
        columnWidth: "45%",
        borderRadius: 2,
      },
    },
    dataLabels: { enabled: false },
    xaxis: {
      categories: chartBuckets.map((d) => d.fullLabel),
      labels: {
        show: true,
        rotate: -45,
        rotateAlways: true,
        style: {
          fontSize: "10px",
          colors: "#6b7280",
        },
        offsetY: 5,
      },
      axisBorder: {
        show: true,
        color: "#BCBDC5",
      },
      axisTicks: {
        show: true,
        color: "#BCBDC5",
      },
      tooltip: { enabled: false },
    },
    yaxis: {
      labels: {
        formatter: (val: number) => formatCurrency(val),
        style: { fontSize: "12px" },
      },
    },
    tooltip: {
      intersect: false,
      followCursor: false,
      y: {
        formatter: (val: number, opts?: any) => {
          const idx = opts?.dataPointIndex ?? 0;
          const dataPoint = chartBuckets[idx];
          const percentageStr = dataPoint?.percentage
            ? ` (${dataPoint.percentage.toFixed(1)}%)`
            : "";
          return `${formatCurrency(val)}${percentageStr}`;
        },
        title: {
          formatter: (_: string, opts?: any) => {
            const idx = opts?.dataPointIndex ?? 0;
            return chartBuckets[idx]?.fullLabel || "";
          },
        },
      },
    },
  };

  return (
    <div className="bg-white border-2 border-border-gray">
      {/* Header */}
      <div className="bg-primary-blue text-white px-4 md:px-6 py-3">
        <h3 className="font-bold text-lg">J-Code A/R</h3>
      </div>

      {/* Controls */}
      <div className="p-4 bg-gray-50 border-b-2 border-border-gray flex flex-wrap gap-3 items-end">
        {/* J-Code Selector */}
        <div className="flex-shrink-0">
          <label className="block text-xs font-medium text-gray-700 mb-1">J-Code</label>
          <SearchableSelect
            options={jCodes}
            value={selectedJCode}
            onChange={setSelectedJCode}
            placeholder="Select J-Code"
            allOptionLabel="All J-Codes"
          />
        </div>

        {/* Date Basis Toggle */}
        <div className="flex-shrink-0">
          <label className="block text-xs font-medium text-gray-700 mb-1">Date Basis</label>
          <div className="flex border-2 border-border-gray h-10">
            <button
              onClick={() => setDateBasis("billing_date")}
              className={`px-4 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                dateBasis === "billing_date"
                  ? "bg-primary-blue text-white"
                  : "bg-white text-black hover:bg-gray-50"
              }`}
            >
              Billing Date
            </button>
            <button
              onClick={() => setDateBasis("service_date")}
              className={`px-4 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap border-l-2 border-border-gray ${
                dateBasis === "service_date"
                  ? "bg-primary-blue text-white"
                  : "bg-white text-black hover:bg-gray-50"
              }`}
            >
              Service Date
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div>
        {isLoading && (
          <div className="text-center p-4 sm:p-6 py-8 text-gray-500">
            Loading J-Code A/R data...
          </div>
        )}

        {error && (
          <div className="text-center p-4 sm:p-6 py-8 text-red-600">
            Failed to load J-Code A/R data. Please try again.
          </div>
        )}

        {!isLoading && !error && (
          <>
            <div className="w-full">
              <ReactApexChart options={options} series={series} type="bar" height={200} />
            </div>

            <div className="max-h-[180px] overflow-y-auto border-t-2 border-border-gray">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-border-gray sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">
                      Date Range
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">
                      Percentage
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-gray">
                  {chartData.map((item, index) => (
                    <tr
                      key={index}
                      className={`hover:bg-gray-50${item.isTotal ? " font-semibold bg-gray-50" : ""}`}
                    >
                      <td className="px-4 py-2 text-sm font-medium">{item.fullLabel}</td>
                      <td className="px-4 py-2 text-sm text-right">{formatCurrency(item.value)}</td>
                      <td className="px-4 py-2 text-sm text-right">
                        {item.isTotal ? "100%" : `${item.percentage.toFixed(1)}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
